import { del, get, patch, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  UiPreferenceCreateSchema,
  UiPreferenceGetSchema,
  UiPreferenceUpdateSchema,
} from "@:openapi";

export interface UiPreferences {
  GET_ONE: {
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<UiPreferenceGetSchema>;
  };
  GET_ALL: {
    RequestQuery?: {
      user_id?: string | null;
      bot_id?: string | null;
      enabled?: boolean | null;
    };
    Response: CamelCasedPropertiesDeep<Array<UiPreferenceGetSchema>>;
  };
  POST: {
    RequestBody: CamelCasedPropertiesDeep<UiPreferenceCreateSchema>;
    Response: CamelCasedPropertiesDeep<UiPreferenceGetSchema>;
  };
  PATCH: {
    RequestBody: CamelCasedPropertiesDeep<UiPreferenceUpdateSchema>;
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<UiPreferenceGetSchema>;
  };
  DELETE: {
    RequestPath: { id: string };
    Response: unknown;
  };
}

export const uiPreferencesQueryKey = createQueryKeys("uiPreferences", {
  getOne: (params: UiPreferences["GET_ONE"]["RequestPath"]) => [params],
  getAll: (isEnabled?: boolean, userId?: string, botId?: string) => [
    isEnabled,
    userId,
    botId,
  ],
});

export const useQueryGetOneUiPreference = (
  params: UiPreferences["GET_ONE"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<UiPreferences["GET_ONE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<UiPreferences["GET_ONE"]["Response"], AxiosError>({
    queryKey: uiPreferencesQueryKey.getOne(params).queryKey,
    queryFn: () => {
      return get<UiPreferences["GET_ONE"]["Response"]>(
        `/api/ui_preferences/${params.id}`,
      );
    },
    ...options,
  });
};

export const useQueryGetAllUiPreferences = (
  isEnabled?: boolean,
  userId?: string,
  botId?: string,
  options?: Omit<
    UseQueryOptions<UiPreferences["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<UiPreferences["GET_ALL"]["Response"], AxiosError>({
    queryKey: uiPreferencesQueryKey.getAll(isEnabled, userId, botId).queryKey,
    queryFn: () => {
      const params: UiPreferences["GET_ALL"]["RequestQuery"] = {};
      if (userId) params.user_id = userId;
      if (botId) params.bot_id = botId;
      if (isEnabled !== undefined) params.enabled = isEnabled;

      return get<UiPreferences["GET_ALL"]["Response"]>(
        "/api/ui_preferences",
        params,
      );
    },
    ...options,
  });
};

export const useMutationCreateUiPreference = (
  options?: UseMutationOptions<
    UiPreferences["POST"]["Response"],
    AxiosError,
    UiPreferences["POST"]["RequestBody"]
  >,
) => {
  return useMutation({
    mutationFn: (data: UiPreferences["POST"]["RequestBody"]) => {
      return post<UiPreferences["POST"]["Response"]>(
        "/api/ui_preferences",
        data,
      );
    },
    ...options,
  });
};

export const useMutationUpdateUiPreference = (
  options?: UseMutationOptions<
    UiPreferences["PATCH"]["Response"],
    AxiosError,
    UiPreferences["PATCH"]["RequestBody"] &
      UiPreferences["PATCH"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (
      data: UiPreferences["PATCH"]["RequestBody"] &
        UiPreferences["PATCH"]["RequestPath"],
    ) => {
      return patch<UiPreferences["PATCH"]["Response"]>(
        `/api/ui_preferences/${data.id}`,
        omit(data, ["id"]),
      );
    },
    ...options,
  });
};

export const useMutationDeleteUiPreference = (
  options?: UseMutationOptions<
    UiPreferences["DELETE"]["Response"],
    AxiosError,
    UiPreferences["DELETE"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (params: UiPreferences["DELETE"]["RequestPath"]) => {
      return del<UiPreferences["DELETE"]["Response"]>(
        `/api/ui_preferences/${params.id}`,
      );
    },
    ...options,
  });
};
